import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { matchesMedia, subscribeMediaQuery } from './mediaQuery'
import { Capacitor, SystemBarType, SystemBars, SystemBarsStyle } from '@capacitor/core'
import {
  type PaletteCode,
  type PaletteDefinition,
  PALETTES,
  PALETTE_KEY,
  VALID_PALETTES,
  loadPalette,
  hasExplicitPalette,
} from './themePalette.ts'

export type { PaletteCode, PaletteDefinition }
export { PALETTES, PALETTE_KEY, VALID_PALETTES, loadPalette }

import { readThemePreference, persistThemePreference, resolveThemePreference, type ThemeMode, type ResolvedTheme } from './themePreference.ts'
export type { ThemeMode, ResolvedTheme }

const THEME_COLOR: Record<ResolvedTheme, string> = { light: '#f8f9fb', dark: '#111317', amoled: '#000000' }

function systemPrefersDark(): boolean {
  return matchesMedia('(prefers-color-scheme: dark)')
}

function resolveTheme(mode: ThemeMode, amoledEnabled: boolean): ResolvedTheme {
  return resolveThemePreference(mode, amoledEnabled, systemPrefersDark())
}

function applyTheme(resolved: ResolvedTheme, palette: PaletteCode = 'ocean') {
  const root = document.documentElement
  root.setAttribute('data-theme', resolved)
  root.setAttribute('data-palette', palette)
  root.style.colorScheme = resolved === 'light' ? 'light' : 'dark'
  const meta = document.querySelector('meta[name="theme-color"]')
  const rgb = getComputedStyle(root).getPropertyValue('--panel-rgb-bg').trim()
  if (meta) meta.setAttribute('content', rgb ? `rgb(${rgb.split(/\s+/).join(', ')})` : THEME_COLOR[resolved])

  if (Capacitor.isNativePlatform()) {
    const style = resolved === 'light' ? SystemBarsStyle.Light : SystemBarsStyle.Dark
    void Promise.all([
      SystemBars.setStyle({ style, bar: SystemBarType.StatusBar }),
      SystemBars.setStyle({ style, bar: SystemBarType.NavigationBar }),
    ]).catch(() => {})
  }
}

export function loadThemeMode(): ThemeMode {
  try { return readThemePreference(localStorage).mode } catch { return 'system' }
}

export function loadAmoledEnabled(): boolean {
  try { return readThemePreference(localStorage).amoled } catch { return false }
}

type ThemeContextValue = {
  mode: ThemeMode
  amoledEnabled: boolean
  palette: PaletteCode
  paletteIsExplicit: boolean
  resolved: ResolvedTheme
  setMode: (mode: ThemeMode) => void
  setAmoledEnabled: (enabled: boolean) => void
  setPalette: (palette: PaletteCode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => loadThemeMode())
  const [amoledEnabled, setAmoledEnabledState] = useState<boolean>(() => loadAmoledEnabled())
  const [palette, setPaletteState] = useState<PaletteCode>(() => loadPalette())
  const [paletteIsExplicit, setPaletteIsExplicit] = useState(hasExplicitPalette)
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(mode, amoledEnabled))

  useEffect(() => {
    const next = resolveTheme(mode, amoledEnabled)
    setResolved(next)
    applyTheme(next, palette)
  }, [mode, amoledEnabled, palette])

  useEffect(() => {
    if (mode !== 'system') return
    return subscribeMediaQuery('(prefers-color-scheme: dark)', () => {
      const next = resolveTheme('system', amoledEnabled)
      setResolved(next)
      applyTheme(next, palette)
    })
  }, [mode, amoledEnabled, palette])

  useEffect(() => {
    const reapply = () => {
      if (document.visibilityState !== 'hidden') {
        const next = resolveTheme(mode, amoledEnabled)
        setResolved(next)
        applyTheme(next, palette)
      }
    }
    document.addEventListener('visibilitychange', reapply)
    window.addEventListener('focus', reapply)
    window.addEventListener('pageshow', reapply)
    return () => {
      document.removeEventListener('visibilitychange', reapply)
      window.removeEventListener('focus', reapply)
      window.removeEventListener('pageshow', reapply)
    }
  }, [mode, amoledEnabled, palette])

  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key !== null && !['scpTheme', 'scpAmoled', PALETTE_KEY].includes(event.key)) return
      const stored = readThemePreference(localStorage)
      setModeState(stored.mode)
      setAmoledEnabledState(stored.amoled)
      setPaletteState(loadPalette())
      setPaletteIsExplicit(hasExplicitPalette())
    }
    window.addEventListener('storage', sync)
    return () => window.removeEventListener('storage', sync)
  }, [])

  const setMode = (next: ThemeMode) => {
    setModeState(next)
    try { persistThemePreference(localStorage, next, amoledEnabled) } catch {}
  }

  const setAmoledEnabled = (enabled: boolean) => {
    setAmoledEnabledState(enabled)
    try { persistThemePreference(localStorage, mode, enabled) } catch {}
  }

  const setPalette = (next: PaletteCode) => {
    setPaletteState(next)
    setPaletteIsExplicit(true)
    try { localStorage.setItem(PALETTE_KEY, next) } catch {}
  }

  const value = useMemo(
    () => ({ mode, amoledEnabled, palette, paletteIsExplicit, resolved, setMode, setAmoledEnabled, setPalette }),
    [mode, amoledEnabled, palette, paletteIsExplicit, resolved]
  )
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme, ThemeProvider içinde kullanılmalı')
  return ctx
}
